import type { ReactElement } from "react";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copyright, ChevronLeft, ChevronRight, List, FileText, Mail, AlertTriangle } from "lucide-react";
import PageLayout from "../components/PageLayout";

/**
 * DMCA policy section
 */
interface DmcaSection {
  id: string;
  title: string;
  shortTitle: string;
  content: string;
}

const LAST_UPDATED = "January 31, 2026";

const SECTIONS: DmcaSection[] = [
  {
    id: "overview",
    title: "1. Digital Millennium Copyright Act Policy",
    shortTitle: "Overview",
    content: `Mytho Labs respects the intellectual property rights of others and expects our users to do the same. This Digital Millennium Copyright Act ("DMCA") Policy outlines our procedures for addressing claims of copyright infringement on the Tellor platform.

We comply with the DMCA and will respond promptly to notices of alleged copyright infringement. This policy explains how to submit a DMCA takedown notice if you believe your copyrighted work has been infringed, as well as how to submit a counter-notification if you believe content was wrongly removed.

We reserve the right to terminate accounts of users who are repeat copyright infringers.`,
  },
  {
    id: "our-commitment",
    title: "2. Our Commitment to Copyright Protection",
    shortTitle: "Commitment",
    content: `Mytho Labs is committed to protecting intellectual property while fostering creativity:

Proactive Measures:
• Our Terms of Service prohibit users from infringing copyrighted content
• We educate users about creating original characters and stories
• Our AI systems are designed to generate original content, not reproduce existing works
• We regularly review content for potential copyright violations

Educational Approach:
• We help users understand the difference between inspiration and infringement
• We provide guidelines for creating original characters versus using existing ones
• We encourage users to develop their own unique storytelling voices
• We offer resources on copyright basics for content creators

Balanced Protection:
• We protect legitimate copyright holders from infringement
• We also protect users from false or abusive copyright claims
• We ensure that our review process is fair and transparent
• We provide clear procedures for both takedowns and counter-notifications`,
  },
  {
    id: "copyright-basics",
    title: "3. Understanding Copyright and Fair Use",
    shortTitle: "Copyright Basics",
    content: `To help our users create appropriate content, here are some copyright basics:

What Is Copyright Protected:
• Original literary works (stories, books, scripts)
• Characters with sufficient detail and originality
• Distinctive visual designs and artwork
• Musical compositions and lyrics
• Photographs and illustrations

What Is NOT Copyright Protected:
• Ideas, concepts, or general themes (you can write about friendship, magic, etc.)
• Common story elements (hero's journey, good vs. evil)
• Facts and historical events
• Titles and short phrases (though they may have trademark protection)
• Works in the public domain

Creating Original Content:
• Develop your own unique characters with original names and traits
• Create your own plot lines and story structures
• Draw inspiration from genres without copying specific works
• Build your own fictional worlds and settings

When You Need Permission:
• Using specific character names from books, movies, or TV shows
• Adapting existing stories or plots
• Using lyrics or substantial quotes from existing works
• Incorporating distinctive visual elements from copyrighted works`,
  },
  {
    id: "ai-generated-content",
    title: "4. AI-Generated Content and Copyright",
    shortTitle: "AI Content",
    content: `Our AI-powered platform raises unique questions about copyright and creativity:

Original AI Creations:
• Stories generated by our AI from user prompts are generally considered original works
• The combination of user creativity (prompts) and AI processing creates new content
• Users own the AI-generated stories created using their original prompts
• However, this ownership is subject to our Terms of Service and any applicable laws

Training Data Considerations:
• Our AI models are trained on diverse datasets that may include copyrighted works
• This training falls under fair use for machine learning purposes
• The AI does not store or reproduce specific copyrighted content
• Generated content is transformative and original, not copied from training data

User Responsibilities:
• Users must ensure their prompts don't directly request copyrighted content
• Users should avoid asking the AI to recreate existing characters or stories
• Users are responsible for the content they generate and share
• Users must respect copyright in their prompts and story concepts

Platform Protections:
• Our content filters help prevent generation of copyrighted characters
• We monitor for potential copyright violations in generated content
• We respond promptly to valid DMCA takedown notices
• We provide education about appropriate use of AI for content creation`,
  },
  {
    id: "takedown-notice",
    title: "5. Filing a DMCA Takedown Notice",
    shortTitle: "Takedown Notice",
    content: `If you believe content on our platform infringes your copyright, you may submit a DMCA takedown notice:

Required Information:
Your notice must include ALL of the following information:

1. Your Contact Information:
   • Full legal name
   • Physical address
   • Phone number
   • Email address

2. Copyright Identification:
   • Identification of the copyrighted work you claim has been infringed
   • If multiple works are involved, a representative list
   • Registration number (if your work is registered with the U.S. Copyright Office)

3. Infringing Material Identification:
   • Specific identification of the material you claim is infringing
   • URL or specific location of the content on our platform
   • Story ID, user account, or other identifying information
   • Sufficient detail to allow us to locate the material

4. Good Faith Statement:
   A statement that you have a good faith belief that the use of the material is not authorized by the copyright owner, its agent, or the law.

5. Accuracy Statement:
   A statement that the information in the notice is accurate and, under penalty of perjury, that you are authorized to act on behalf of the copyright owner.

6. Electronic Signature:
   Your physical or electronic signature.

How to Submit:
Email your complete notice to: dmca@mytho-labs.com
Subject line: "DMCA Takedown Notice"

Processing Time:
• We review DMCA notices within 24-48 hours
• Valid notices result in content removal within 24 hours
• Users are notified when their content is removed
• Invalid or incomplete notices will be rejected with explanation`,
  },
  {
    id: "counter-notification",
    title: "6. Filing a DMCA Counter-Notification",
    shortTitle: "Counter-Notice",
    content: `If you believe content was wrongly removed due to a DMCA takedown notice, you may file a counter-notification:

When to File a Counter-Notification:
• You believe the removal was based on misidentification of the material
• You believe the removal was based on a mistake about the law
• You believe the original notice was fraudulent or made in bad faith
• You have authorization to use the material from the copyright holder
• Your use constitutes fair use under copyright law

Required Information:
Your counter-notification must include:

1. Your Contact Information:
   • Full legal name
   • Physical address
   • Phone number
   • Email address

2. Removed Material Identification:
   • Identification of the material that was removed
   • The location where the material appeared before removal
   • Story ID or other identifying information

3. Good Faith Statement:
   A statement under penalty of perjury that you have a good faith belief that the material was removed as a result of mistake or misidentification.

4. Consent to Jurisdiction:
   A statement that you consent to the jurisdiction of the Federal District Court for your district (or if outside the U.S., any judicial district in which Mytho Labs may be found).

5. Service of Process:
   A statement that you will accept service of process from the person who provided the takedown notice or their agent.

6. Electronic Signature:
   Your physical or electronic signature.

How to Submit:
Email your complete counter-notification to: dmca@mytho-labs.com
Subject line: "DMCA Counter-Notification"

What Happens Next:
• We forward your counter-notification to the original complainant
• If they don't file a court action within 10-14 business days, we may restore the content
• If they do file a court action, the matter proceeds through the legal system
• We may restore content sooner if both parties agree`,
  },
  {
    id: "safe-harbor",
    title: "7. Safe Harbor Protection and Platform Policies",
    shortTitle: "Safe Harbor",
    content: `Mytho Labs operates under the DMCA's safe harbor provisions:

Our Safe Harbor Status:
• We qualify for DMCA safe harbor protection as a service provider
• We don't actively monitor user-generated content for copyright infringement
• We respond expeditiously to valid DMCA takedown notices
• We have designated an agent to receive copyright notices

User Account Termination:
• We may terminate accounts of users who repeatedly infringe copyright
• Repeat offenses include multiple valid DMCA takedown notices
• We consider the totality of circumstances in termination decisions
• Appeals may be submitted for account termination decisions

Platform Limitations:
• We are not responsible for determining copyright ownership
• We cannot provide legal advice about copyright matters
• We follow DMCA procedures but cannot guarantee outcomes
• Complex copyright disputes should be resolved in court

Good Faith Requirements:
• All DMCA notices must be submitted in good faith
• False or fraudulent notices may result in legal liability
• We may terminate accounts that submit abusive copyright claims
• We reserve the right to ignore notices that appear fraudulent`,
  },
  {
    id: "special-considerations",
    title: "8. Special Considerations for Children's Content",
    shortTitle: "Children's Content",
    content: `Given our focus on children's storytelling, we have additional considerations:

Educational Fair Use:
• Educational use of copyrighted material may qualify for fair use protection
• Teachers and students may have broader rights to use copyrighted material
• However, commercial platforms like ours have more limited fair use rights
• When in doubt, create original content rather than relying on fair use

Popular Characters:
• Many popular children's characters are strongly protected by copyright
• Avoid using specific character names from books, movies, or TV shows
• Create original characters inspired by archetypes rather than copying existing ones
• Remember that character names like "Mickey Mouse," "Harry Potter," etc. are protected

Public Domain Works:
• Some classic children's stories are in the public domain
• These include works published before 1928 and some later works
• However, specific adaptations or illustrations may still be copyrighted
• When using public domain works, ensure you're not copying protected adaptations

International Considerations:
• Copyright laws vary by country
• We primarily operate under U.S. copyright law
• Content may be subject to different rules in different jurisdictions
• When in doubt, consult with appropriate legal counsel`,
  },
  {
    id: "best-practices",
    title: "9. Best Practices for Content Creators",
    shortTitle: "Best Practices",
    content: `Follow these guidelines to avoid copyright issues:

Creating Original Content:
• Develop your own unique characters with original names and backstories
• Create original plot lines and story structures
• Use generic settings (forest, castle, space) rather than specific fictional worlds
• Draw inspiration from themes without copying specific implementations

Character Development:
• Instead of "Superman," create "a superhero with unique powers"
• Instead of "Elsa," create "an ice princess with her own story"
• Instead of "Pikachu," create "a friendly electric creature"
• Focus on universal themes like friendship, courage, and kindness

Story Elements You Can Use:
• Common fairy tale structures (hero's journey, rescue missions)
• Universal themes (good vs. evil, growing up, learning lessons)
• Generic settings (magical kingdoms, space adventures, underwater worlds)
• Archetypal characters (wise mentors, brave heroes, comic sidekicks)

When in Doubt:
• Ask yourself: "Did I create this character/story/element myself?"
• If you're adapting something, do you have permission?
• Would the copyright owner likely object to this use?
• Am I adding enough of my own creativity to make this transformative?

Resources for Creators:
• Use our character creation guides for inspiration
• Check our story prompt library for original ideas
• Research public domain works for inspiration
• Consult copyright resources if you have questions`,
  },
  {
    id: "false-claims",
    title: "10. Protection Against False Claims",
    shortTitle: "False Claims",
    content: `We protect our users against abusive or false copyright claims:

Invalid DMCA Notices:
• Claims against content that doesn't contain copyrighted material
• Claims by parties who don't own the copyright
• Claims that misrepresent fair use or other legal exceptions
• Claims made without good faith belief in infringement

Consequences for False Claims:
• False DMCA notices may result in legal liability under Section 512(f)
• We may ban users who repeatedly submit false claims
• False claimants may be liable for damages and attorney fees
• We cooperate with legitimate counter-notifications

Protection Measures:
• We review DMCA notices for completeness and validity
• We provide counter-notification procedures for wrongful takedowns
• We maintain records of notices and responses
• We may refuse to process clearly invalid notices

User Rights:
• Users have the right to contest wrongful takedowns
• Users may seek damages for false DMCA claims
• Users can appeal account actions related to copyright claims
• Users receive copies of DMCA notices affecting their content`,
  },
  {
    id: "contact",
    title: "11. Contact Information",
    shortTitle: "Contact",
    content: `For all copyright-related matters, please contact our designated DMCA agent:

DMCA Agent:
Mytho Labs Legal Department
Email: dmca@mytho-labs.com
Subject Line Requirements:
• For takedown notices: "DMCA Takedown Notice"
• For counter-notifications: "DMCA Counter-Notification"
• For general copyright questions: "Copyright Inquiry"

Mailing Address:
Mytho Labs
Attention: DMCA Agent
[Physical address to be provided]

Important Notes:
• Only send DMCA-related communications to dmca@mytho-labs.com
• For general support issues, use support@mytho-labs.com
• For legal notices unrelated to DMCA, use legal@mytho-labs.com
• Response time: 24-48 hours for DMCA notices, 3-5 days for general inquiries

Additional Resources:
• U.S. Copyright Office: www.copyright.gov
• DMCA information: www.dmca.org
• Fair use guidelines: Available through the U.S. Copyright Office
• Legal advice: Consult qualified intellectual property attorneys for complex matters

We encourage open communication and will work with copyright holders to resolve issues efficiently and fairly while protecting the legitimate rights of our users.`,
  },
];

/**
 * DmcaPolicyPage Component
 *
 * DMCA and copyright policy page
 */
const DmcaPolicyPage = (): ReactElement => {
  const navigate = useNavigate();
  const [showToc, setShowToc] = useState<boolean>(false);

  const scrollToSection = (id: string): void => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setShowToc(false);
  };

  return (
    <PageLayout>
      <div className="min-h-screen pb-16" style={{ background: "var(--st-bg-page)" }}>
        {/* Back Button */}
        <div className="max-w-3xl mx-auto px-4 pt-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm mb-4 transition-all hover:opacity-70"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Page Header Card */}
        <div className="max-w-3xl mx-auto px-4">
          <div
            className="rounded-2xl p-6 mb-6"
            style={{
              background: "var(--st-bg-surface)",
              border: "1px solid var(--st-border-light)",
              boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-16 h-16 mb-4 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              >
                <Copyright className="w-8 h-8 text-white" />
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                DMCA & Copyright Policy
              </h1>
              <p
                className="text-sm mb-2"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Protecting intellectual property while fostering creativity
              </p>
              <p
                className="text-xs"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Last updated: {LAST_UPDATED}
              </p>

              {/* Table of Contents Toggle - Mobile */}
              <button
                onClick={() => setShowToc(!showToc)}
                className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] md:hidden"
                style={{
                  background: "var(--st-bg-page)",
                  border: "1px solid var(--st-border-light)",
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                <List className="w-4 h-4" />
                {showToc ? "Hide" : "Show"} Contents
              </button>
            </div>
          </div>
        </div>

        {/* Important Notice Banner */}
        <div className="max-w-3xl mx-auto px-4 mb-6">
          <div
            className="flex items-start gap-3 p-4 rounded-xl"
            style={{
              background: "var(--st-bg-warning)",
              border: "1px solid var(--st-border-medium)",
            }}
          >
            <AlertTriangle
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              style={{ color: "var(--st-primary)" }}
            />
            <div>
              <p
                className="text-sm font-semibold mb-1"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Copyright Notice
              </p>
              <p
                className="text-xs"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                We respect intellectual property rights. If you believe your copyright has been infringed, please follow the procedures outlined in{" "}
                <button
                  onClick={() => scrollToSection("takedown-notice")}
                  className="underline font-semibold hover:opacity-70"
                >
                  Section 5
                </button>{" "}
                to submit a DMCA takedown notice.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile TOC Dropdown */}
        {showToc && (
          <div className="max-w-3xl mx-auto px-4 mb-6 md:hidden">
            <div
              className="rounded-xl p-4"
              style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
            >
              <div className="space-y-1">
                {SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  >
                    {section.shortTitle}
                    <ChevronRight className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex gap-6">
            {/* Desktop Table of Contents - Sticky Sidebar */}
            <div className="hidden md:block w-48 flex-shrink-0">
              <div
                className="sticky top-24 rounded-xl p-4"
                style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
              >
                <p
                  className="text-xs font-semibold mb-3 uppercase tracking-wide"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  DMCA Policy
                </p>
                <div className="space-y-1">
                  {SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="w-full text-left px-2 py-1.5 rounded-lg text-xs transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-heading)",
                      }}
                    >
                      {section.shortTitle}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div
                className="rounded-xl p-6 md:p-8"
                style={{
                  background: "var(--st-bg-surface)",
                  border: "1px solid var(--st-border-light)",
                  boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
                }}
              >
                {/* Sections */}
                <div className="space-y-8">
                  {SECTIONS.map((section) => (
                    <div key={section.id} id={section.id} className="scroll-mt-24">
                      <h2
                        className="text-lg font-bold mb-3"
                        style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                      >
                        {section.title}
                      </h2>
                      <div
                        className="text-sm leading-relaxed whitespace-pre-wrap"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-heading)",
                        }}
                      >
                        {section.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => {
                navigate("/terms");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Terms of Service
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => {
                navigate("/privacy");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Privacy Policy
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => {
                navigate("/content-policy");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Content Policy
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => {
                navigate("/contact");
                window.scrollTo(0, 0);
              }}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-[var(--st-bg-surface)]"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default DmcaPolicyPage;